import { InPlaceEditorProps, Labeled, NumberField, NumberInput, Validator } from "react-admin";
import { AegleInPlaceEditor } from "./AegleInPlaceEditor";

/**
 * In-place editor for numeric fields.
 *
 * Displays a formatted number using {@link NumberField}, and allows editing with {@link NumberInput}.
 * Wrapped in a {@link Labeled} component to optionally show a field label.
 *
 * @param source - The field name to bind to
 * @param label - Optional label displayed above the input
 * @param readOnly - If true, disables editing and shows display only
 * @param rest - Additional props passed to {@link InPlaceEditor} and {@link NumberInput}
 */
const InPlaceNumberEditor = ({
  source,
  label,
  readOnly = false,
  validate,
  numberFieldOptions,
  ...rest
}: {
  source: string;
  label?: string;
  readOnly?: boolean;
  validate?: Validator | Validator[];
  numberFieldOptions?: Intl.NumberFormatOptions;
} & Omit<InPlaceEditorProps, "input" | "source">) => {
  // Read-only mode: just show the display component
  if (readOnly) {
    return (
      <Labeled label={label}>
        <NumberField
          source={source}
          variant="body1"
          sx={{ display: "block", mb: 0.5 }}
          emptyText="-"
          options={numberFieldOptions}
        />
      </Labeled>
    );
  }

  return (
    <Labeled label={label}>
      <AegleInPlaceEditor
        source={source}
        editor={
          <NumberInput
            {...rest}
            source={source}
            variant="standard"
            size="small"
            margin="none"
            label={false}
            helperText={validate ? undefined : false}
            validate={validate}
            autoFocus
            sx={{
              "& .MuiInput-root": { marginTop: 0 },
            }}
          />
        }
        {...rest}
      >
        <NumberField
          source={source}
          variant="body1"
          sx={{ display: "block", mb: 0.5 }}
          emptyText="-"
          options={numberFieldOptions}
        />
      </AegleInPlaceEditor>
    </Labeled>
  );
};

export default InPlaceNumberEditor;
